import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Github, Mail, Twitter } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';

interface LinkItem { to: string; label: string; external?: boolean }

export function SiteFooter() {
  const { t } = useTranslation();

  const product: LinkItem[] = [
    { to: '/pricing',  label: t('footer.col.product.pricing') },
    { to: '/compare',  label: t('footer.col.product.compare') },
    { to: '/demo',     label: t('footer.col.product.demo') },
    { to: '/api-docs', label: t('footer.col.product.api_docs', { defaultValue: 'API docs' }) },
    { to: '/changelog',label: t('footer.col.product.changelog') },
    { to: '/security', label: t('footer.col.product.security') },
    { to: '/status',   label: t('footer.col.product.status') },
  ];

  const company: LinkItem[] = [
    { to: '/about',    label: t('footer.col.company.about') },
    { to: '/contact',  label: t('footer.col.company.contact') },
    { to: '/careers',  label: t('footer.col.company.careers') },
    { to: '/press',    label: t('footer.col.company.press') },
  ];

  const legal: LinkItem[] = [
    { to: '/terms',          label: t('footer.col.legal.terms') },
    { to: '/privacy',        label: t('footer.col.legal.privacy') },
    { to: '/cookies',        label: t('footer.col.legal.cookies') },
    { to: '/dpa',            label: t('footer.col.legal.dpa') },
    { to: '/subprocessors',  label: t('footer.col.legal.subprocessors') },
    { to: '/aup',            label: t('footer.col.legal.aup') },
  ];

  const resources: LinkItem[] = [
    { to: '/security#vulnerability', label: t('footer.col.resources.security_disclosure') },
    { to: '/contact#sales',          label: t('footer.col.resources.contact_sales') },
    { to: '/contact#support',        label: t('footer.col.resources.support') },
  ];

  return (
    <footer className="relative border-t border-border bg-card/60 backdrop-blur mt-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-2">
            <Link to="/" className="inline-flex items-center gap-2.5">
              <BrandMark />
              <span className="font-display text-lg font-semibold tracking-tight">{t('brand.name')}</span>
            </Link>
            <p className="mt-3 text-sm text-muted-foreground max-w-xs text-pretty">
              {t('footer.tag')}
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              {t('footer.address')}
            </p>
            <div className="mt-5 flex items-center gap-2">
              <a href="mailto:hello@keenez.com" aria-label="email"
                 className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted transition">
                <Mail className="h-4 w-4" />
              </a>
              <a href="https://x.com/keenez" target="_blank" rel="noreferrer" aria-label="x.com"
                 className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted transition">
                <Twitter className="h-4 w-4" />
              </a>
              <a href="https://github.com/keenezrp" target="_blank" rel="noreferrer" aria-label="github"
                 className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted transition">
                <Github className="h-4 w-4" />
              </a>
            </div>
          </div>

          <FooterColumn title={t('footer.col.product.title')} items={product} />
          <FooterColumn title={t('footer.col.company.title')} items={company} />
          <FooterColumn title={t('footer.col.legal.title')} items={legal} />
          <FooterColumn title={t('footer.col.resources.title')} items={resources} />
        </div>

        <div className="mt-10 pt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-muted-foreground">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:gap-5">
            <p className="text-xs text-muted-foreground">{t('footer.builtIn')}</p>
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, items }: { title: string; items: LinkItem[] }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.16em] text-foreground/85 font-semibold mb-3">{title}</p>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.to + it.label}>
            {it.external ? (
              <a href={it.to} target="_blank" rel="noreferrer"
                 className="group inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition">
                {it.label}
                <ArrowUpRight className="h-3 w-3 opacity-60 group-hover:opacity-100" />
              </a>
            ) : (
              <Link to={it.to}
                    className="text-sm text-muted-foreground hover:text-foreground transition">
                {it.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function BrandMark() {
  return (
    <img
      src="/logo.png"
      alt="KeenEZ"
      className="h-8 w-8 rounded-xl object-contain"
      width={32}
      height={32}
    />
  );
}
